import { getModuleGateAvailability } from '@/services/muou-biz';
import type { PlanModuleGateRowVO } from '@/types';
import {
  isModuleGated,
  moduleGateReasonOf,
  type ModuleGateReason,
  type ModuleGateRows,
} from '@/utils/moduleGate';
import { onModuleGateDenied } from '@/utils/http';
import { create } from 'zustand';

/**
 * 模块门控：本会当前档位与每个模块开没开，登录后取一次。
 *
 * 数据只用于**展示**（侧栏置灰、拦截页），判定仍在 platform 一处；前端不按档位名、
 * 价格或权限码自己推。**取不到按全亮放行**——与网关「门控服务不可用时放行」同口径，
 * 失败也不挡登录：真闸是网关与服务端的 403。
 */
interface ModuleGateState {
  rows: ModuleGateRows;
  currentPlanCode: string | null;
  currentPlanName: string | null;
  /** 至少成功取到过一次；`load()` 只在这种情况下才不再发请求 */
  loaded: boolean;
  loading: boolean;
  /** 登录后加载一次；已加载或在飞时直接复用，不发第二个请求 */
  load: () => Promise<void>;
  /** 升级 / 缴付到账后强制重取（网关有 30 秒缓存，前端不等它自然过期） */
  refresh: () => Promise<void>;
  /** 退出或会话失效时清空，不留上一个人的档位 */
  reset: () => void;
  gated: (moduleCode?: string | null) => boolean;
  reason: (moduleCode?: string | null) => ModuleGateReason | null;
}

const EMPTY = {
  rows: {} as ModuleGateRows,
  currentPlanCode: null,
  currentPlanName: null,
  loaded: false,
  loading: false,
};

/** 在飞请求只留一个；退出时递增版本号，晚到的回包不覆盖新会话 */
let inflight: Promise<void> | null = null;
let revision = 0;

const indexRows = (modules: PlanModuleGateRowVO[]): ModuleGateRows =>
  Object.fromEntries(modules.map((row) => [row.moduleCode, row]));

export const useModuleGateStore = create<ModuleGateState>((set, get) => {
  const run = (): Promise<void> => {
    if (inflight) return inflight;
    const mine = revision;
    set({ loading: true });
    const request = getModuleGateAvailability()
      .then((resp) => {
        if (mine !== revision) return;
        set({
          rows: indexRows(resp.modules),
          currentPlanCode: resp.currentPlanCode,
          currentPlanName: resp.currentPlanName,
          loaded: true,
        });
      })
      .catch(() => {
        // 取不到按全亮放行：首次失败保持空表，已有数据时保留上一份
      })
      .finally(() => {
        if (inflight === request) inflight = null;
        if (mine === revision) set({ loading: false });
      });
    inflight = request;
    return request;
  };

  return {
    ...EMPTY,
    load: () => {
      if (get().loaded) return Promise.resolve();
      return run();
    },
    refresh: () => run(),
    reset: () => {
      revision += 1;
      inflight = null;
      set({ ...EMPTY });
    },
    gated: (moduleCode) => isModuleGated(get().rows, moduleCode),
    reason: (moduleCode) => moduleGateReasonOf(get().rows, moduleCode),
  };
});

/**
 * 任何接口回 18403001（模块未开通）时补刷一次门控：别的机器上刚升级、或本机缓存
 * 落后于网关时，导航不用等下一次登录才解锁。http 不反向依赖这个 store，只发信号。
 */
onModuleGateDenied(() => {
  void useModuleGateStore.getState().refresh();
});
